import { useSafeBrowserLocation, useSafeSearch, useSafeFragment } from "../lib/router-safe";
import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { X, FileText, Wrench, Building2, Megaphone, Briefcase, FolderKanban, ReceiptText, Package, ChevronRight, Plus } from "lucide-react";
import { postTypes } from "../data/mock";
import { postHref } from "../lib/postNavigation";
import { useEscapeKey } from "../lib/useEscapeKey";
import { useI18n } from "../lib/i18n";

const icons: Record<string, any> = { FileText, Wrench, Building2, Megaphone, Briefcase, FolderKanban, ReceiptText, Package };
// [posting.publication-access](../../../docs/product/marketplace.md#posting-publication-access): Temporarily hide company entries in this menu; direct posting flows retain the full catalog.
const VISIBLE_POST_TYPES = postTypes.filter(({ key }) => key !== "company" && key !== "company-service");

/** Curved sides keep the sheet's full-width top connected to its narrowing neck. */
function genieClip({ width, height, neckWidth, topWidth = width, shoulder = 0 }: {
  width: number;
  height: number;
  neckWidth: number;
  topWidth?: number;
  shoulder?: number;
}) {
  const leftTop = (width - topWidth) / 2;
  const rightTop = (width + topWidth) / 2;
  const leftNeck = (width - neckWidth) / 2;
  const rightNeck = (width + neckWidth) / 2;
  const curveHeight = height - shoulder;
  return `path('M ${leftTop} 0 L ${rightTop} 0 L ${rightTop} ${shoulder} C ${rightTop} ${shoulder + curveHeight * 0.45}, ${rightNeck} ${shoulder + curveHeight * 0.48}, ${rightNeck} ${height} L ${leftNeck} ${height} C ${leftNeck} ${shoulder + curveHeight * 0.48}, ${leftTop} ${shoulder + curveHeight * 0.45}, ${leftTop} ${shoulder} Z')`;
}

type PostMenuProps = {
  open: boolean;
  /** Called after exit motion so the caller keeps the menu mounted until then. */
  onClose: () => void;
  /** Opts the bottom-navigation caller into motion anchored to its plus button. */
  triggerRef?: RefObject<HTMLButtonElement>;
};

export function PostMenu({ open, onClose, triggerRef }: PostMenuProps) {
  const [location, navigate] = useSafeBrowserLocation();
  const { t: translate } = useI18n();
  const search = useSafeSearch();
  const fragment = useSafeFragment();
  const returnTo = (search ? `${location}?${search}` : location) + fragment;
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const originRef = useRef<HTMLDivElement>(null);
  const animationsRef = useRef<{ panel: Animation; backdrop: Animation } | null>(null);
  const closingRef = useRef(false);

  useLayoutEffect(() => {
    if (!open || !triggerRef?.current || !originRef.current) return;
    // The real tab button keeps focus ownership; this decorative copy stays
    // above the moving sheet and backdrop without raising the other tabs.
    const positionOrigin = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      if (!trigger || !originRef.current) return;
      Object.assign(originRef.current.style, {
        left: `${trigger.left}px`, top: `${trigger.top}px`,
        width: `${trigger.width}px`, height: `${trigger.height}px`, visibility: "visible",
      });
    };
    const handleResize = () => {
      positionOrigin();
      const animations = animationsRef.current;
      if (!animations) return;
      // Old viewport coordinates cannot describe the resized sheet. Settle an
      // ongoing dismissal, or retain the open menu without the stale mask.
      if (closingRef.current) animations.panel.finish();
      else {
        animations.panel.cancel();
        animations.backdrop.cancel();
        animationsRef.current = null;
      }
    };
    positionOrigin();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, triggerRef]);

  useLayoutEffect(() => {
    if (!open || !triggerRef?.current || !panelRef.current || !backdropRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    if (!trigger.width || !trigger.height) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const panel = panelRef.current.getBoundingClientRect();
    const offsetX = trigger.left + trigger.width / 2 - panel.left - panel.width / 2;
    const offsetY = trigger.top + trigger.height / 2 - panel.bottom;
    const timing = { duration: 520, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "both" } as const;
    const shape = { width: panel.width, height: panel.height, neckWidth: trigger.width };
    // At rest the taper occupies less than a pixel. Its tip stays button-wide
    // throughout interpolation; no horizontal scaling can widen that tip.
    const fullClip = genieClip({ ...shape, shoulder: panel.height - 0.01 });

    // Keep the curved neck at the plus until the sheet reaches its full height,
    // then widen and lift it into place, like a window emerging from the Dock.
    // Retaining these animations lets dismissal reverse even a partial opening.
    const panelAnimation = panelRef.current.animate([
      { transform: `translate(${offsetX}px, ${offsetY}px) scale(1, ${trigger.height / 2 / panel.height})`, opacity: 0, clipPath: genieClip({ ...shape, topWidth: trigger.width }) },
      { transform: `translate(${offsetX}px, ${offsetY}px) scale(1, ${1 + offsetY / panel.height})`, opacity: 1, clipPath: genieClip(shape), offset: 0.38 },
      { transform: `translate(${offsetX * 0.45}px, ${offsetY * 0.45}px) scale(1, ${1 + offsetY * 0.45 / panel.height})`, opacity: 1, clipPath: genieClip({ ...shape, shoulder: panel.height * 0.55 }), offset: 0.65 },
      { transform: "translate(0, 0) scale(1, 1)", opacity: 1, clipPath: fullClip },
    ], timing);
    const backdropAnimation = backdropRef.current.animate([{ opacity: 0 }, { opacity: 1 }], timing);
    animationsRef.current = { panel: panelAnimation, backdrop: backdropAnimation };
    return () => {
      panelAnimation.cancel();
      backdropAnimation.cancel();
      animationsRef.current = null;
      closingRef.current = false;
    };
  }, [open, triggerRef]);

  function closeMenu(destination?: string) {
    // Keep the scroll lock through dismissal and accept only the first action.
    if (closingRef.current) return;
    const finishClose = () => {
      onClose();
      if (destination) navigate(destination);
      else triggerRef?.current?.focus({ preventScroll: true });
    };
    const animations = animationsRef.current;
    if (!animations || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finishClose();
      return;
    }
    closingRef.current = true;
    animations.panel.onfinish = finishClose;
    animations.panel.reverse();
    animations.backdrop.reverse();
  }

  useEscapeKey(() => closeMenu(), open);
  useEffect(() => {
    if (!open) return;
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] pb-28 sm:pt-4 sm:pb-4 overflow-y-auto overscroll-contain" onClick={() => closeMenu()}>
      <div ref={backdropRef} className="fixed inset-0 bg-navy/50 backdrop-blur-sm" aria-hidden="true" />
      <div ref={panelRef} className="card relative origin-bottom w-full max-w-2xl p-5 sm:p-6 mb-[max(1rem,env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-extrabold text-navy">{translate("posting.menu.heading")}</h3>
          <button onClick={() => closeMenu()} aria-label={translate("action.close", "Close")} className="btn-ghost h-8 w-8 p-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {VISIBLE_POST_TYPES.map((t) => {
            const Icon = icons[t.icon];
            return (
              <button
                key={t.key}
                onClick={() => closeMenu(postHref(t.key, returnTo, "details"))}
                className="flex items-center gap-3 p-3 rounded-lg border border-line hover:border-navy-500 hover:bg-navy-50 text-left transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-navy text-sm">{translate(t.translationKey || `posting.menu.${t.key === "company-service" ? "companyService" : t.key}`, t.title)}</div>
                  <div className="text-xs text-mut">{translate(`posting.menu.${t.key === "company-service" ? "companyService" : t.key}.description`, t.sub)}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-mut group-hover:text-navy" />
              </button>
            );
          })}
        </div>
        <p className="text-xs text-mut mt-4 text-center">{translate("posting.menu.footer")}</p>
      </div>
      {triggerRef && (
        <div ref={originRef} aria-hidden="true" className="post-menu-origin invisible fixed z-10 grid place-items-center rounded-full bg-brand-red text-white">
          <Plus className="h-6 w-6" strokeWidth={2} />
        </div>
      )}
    </div>
  );
}
